import { useEffect, useRef } from "react";
import { Modal, Button, Label, FileInput, TextInput, Tabs, Card } from "flowbite-react";
import { HiUpload, HiPhotograph, HiVideoCamera, HiArchive } from "react-icons/hi";
import useFiles from "../hooks/useFiles";
import { Attachment, AttachmentType } from "../types/attachment";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { toast } from 'react-toastify';
import { useForm } from "react-hook-form";
import { useAuth } from "../context/authContext";
import FileList from "./FileList";
interface Props {
    open: boolean;
    onClose?: () => void;
    allowedFiles?: string[];
    onSelect: (file: Attachment) => void;
}

interface MimeToFileTypeMap {
  [mime: string]: AttachmentType;
}

interface State {
  show: boolean;
  drag: boolean;
  isPreview: boolean;
  file: File | null;
  previewFile: string | undefined;
  uploading: boolean;
}

const FileManager = ({open, onClose, allowedFiles  = ['image/png','image/jpg','image/jpeg','image/gif','application/pdf','application/mp3','video/mp4'], onSelect} : Props) => {
  const { permissions } = useAuth()

  const canCreate = permissions.find(item => item.slug === 'crear-archivos')
  
  const [state, setState] = useReducerAsState<State>({
    show: false,
    drag: false,
    isPreview: false,
    file: null,
    previewFile: undefined,
    uploading: false,
  })  

  const {register, handleSubmit} = useForm()

  const { uploadFile } = useFiles()

  const dropRef : any = useRef(null);
  const fileRef: any = useRef(null);

  const initializeDropZone = () => {
        if(open && canCreate){
            const div = dropRef.current;
            div.addEventListener('dragenter', handleDragIn);
            div.addEventListener('dragleave', handleDragOut);
            div.addEventListener('dragover', handleDrag);
            div.addEventListener('drop', handleDrop);

            return () => {
                div.removeEventListener('dragenter', handleDragIn);
                div.removeEventListener('dragleave', handleDragOut);
                div.removeEventListener('dragover', handleDrag);
                div.removeEventListener('drop', handleDrop);
            };
        }
    }

    // eslint-disable-next-line
    useEffect(() => setState({ show: open }) ,[open])

    useEffect(() => {
        setTimeout(() => initializeDropZone(),500)
        // eslint-disable-next-line
    }, [dropRef]);

    const handleClose = () => {
        setState({ show: false })
        if(onClose){
            onClose()
        }
    }

    let dragCounter = 0;

    const handleChange = (event: any) => {
        event.stopPropagation();
        event.preventDefault();
        const files = event.target.files;
        const filteredFiles: any[] = [];
        Object.keys(files).forEach((file) => {
          if (allowedFiles.includes(files[file].type)) {
            filteredFiles.push(files[file]);
          }
        });

        const reader = new FileReader();
        reader.onloadend = () => {
          setState({ previewFile: reader.result?.toString() })  
        };
        reader.readAsDataURL(filteredFiles[0]);
        setState({ file: filteredFiles[0], isPreview: true })
      };

  const onSubmit = async (data: any) => {
    if(!state.file) return

    const fileTypes: MimeToFileTypeMap = {
      'image/png': AttachmentType.IMAGE,
      'image/jpeg': AttachmentType.IMAGE,
      'image/gif': AttachmentType.IMAGE,
      'image/jpg': AttachmentType.IMAGE,
      'application/pdf': AttachmentType.PDF,
      'video/mp4': AttachmentType.VIDEO,
      'audio/mpeg': AttachmentType.AUDIO,
      'audio/ogg': AttachmentType.AUDIO,
      'audio/mp3': AttachmentType.AUDIO,
    }

    const fileMime = fileTypes[state.file.type] 

    if(!fileMime) {
      console.error('File type not allowed', state.file.type)
      return
    }

    setState({ uploading: true })

    const file = await uploadFile(state.file, {
      title: data.title || state.file.name,
      foot: data.foot || "",
      source: data.author || "",
      type: fileMime,
    })

    if(!file){
      setState({ uploading: false })
      toast("Ocurrió un error al subir el archivo",{
        type:"error"
      })
      return
    }

    toast("Archivo subido correctamente",{
      type:"success",
    })
    setState({ isPreview: false, uploading: false })
  }

  const handleDrag = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setState({ drag: true })
    }
  };

  const handleDragOut = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter--;
    if (dragCounter === 0) {
      setState({ drag: false })
    }
  };

  const handleDrop = (e: any) => {
    
    e.preventDefault();
    e.stopPropagation();
    setState({ drag: false })
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = e.dataTransfer.files;
      const filteredFiles: any[] = [];

      Object.keys(files).forEach((file) => {
        if (allowedFiles.includes(files[file].type)) {
          filteredFiles.push(files[file]);
        }
      });

      dragCounter = 0;

      if (filteredFiles.length > 0) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setState({ previewFile: reader.result?.toString() })
        };
        reader.readAsDataURL(filteredFiles[0]);
        setState({ file: filteredFiles[0], isPreview: true})
      }
    }
  };

  const uploadFileTab = () => {
    if(canCreate) {
      return <div className="flex w-full items-center justify-center py-0 px-4">
        <Label
            htmlFor="dropzone-file"
            className="flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-gray-500 dark:hover:bg-gray-600"
            
        >
            <div className="flex flex-col items-center justify-center pb-6 pt-5" ref={dropRef}>
            <svg
                className="mb-4 h-8 w-8 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
            >
                <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
            </svg>
            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="font-semibold">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">SVG, PNG, JPG or GIF (MAX. 800x400px)</p>
            </div>
            <FileInput 
                ref={fileRef} 
                id="dropzone-file" 
                className="hidden"  
                accept=".jpg,.jpeg,.png,.mp4,.mp3,.gif,.mpeg,.ogg,.pdf"
                onChange={handleChange} />
        </Label>
    </div>
    }

    return <Card>
      <p>No tienes permiso para subir nuevos archivos.</p>
    </Card>
    
  }

  const renderContent = () => {
    if(state.isPreview){
        return <div className="flex w-full items-center justify-center p-6">
        <div className="flex w-full flex-col gap-4">
            <div className="mb-2 block">                
                {state.file?.type.includes('image/') && <img src={state.previewFile} width={250} height={250} alt="preview" />}
            </div>
            <div>
                <div className="mb-2 block">
                <Label htmlFor="title" value="Título" />
                </div>
                <TextInput {...register("title")} id="title" type="text" />
            </div>
            <div>
                <div className="mb-2 block">
                <Label htmlFor="author" value="Autor" />
                </div>
                <TextInput {...register("author")} id="author" type="text" />
            </div>
            <div>
                <div className="mb-2 block">
                <Label htmlFor="author" value="Pié de archivo" />
                </div>
                <TextInput {...register("foot")} id="foot" type="text" />
            </div>
        </div>
    </div>
        
    }

    return <div className="flex w-full">
        <Tabs aria-label="Tabs with underline" style="fullWidth" className="w-full">
            <Tabs.Item active title="Subir archivo" icon={HiUpload}>
                {uploadFileTab()}
            </Tabs.Item>
            <Tabs.Item title="Imágenes" icon={HiPhotograph}>
                <FileList type="image" page={1} onSelect={onSelect} />
            </Tabs.Item>
            <Tabs.Item title="Videos" icon={HiVideoCamera}>
              <FileList type="video" page={1} onSelect={onSelect} />
            </Tabs.Item>
            {/*<Tabs.Item title="Audios" icon={HiMusicNote}>
              <FileList type="audio" page={1} onSelect={onSelect} />
             </Tabs.Item>*/}
            <Tabs.Item title="Archivos" icon={HiArchive}>
              <FileList type="pdf" page={1}  onSelect={onSelect}/>
            </Tabs.Item>
        </Tabs>
    </div>

  }

    return <Modal size="5xl" show={state.show} onClose={() => {
      if(!state.uploading){
        handleClose()
      }
    }}>
        <Modal.Header>Administrador de archivos</Modal.Header>
        <Modal.Body className="p-0">
            {renderContent()}
        </Modal.Body>
        <Modal.Footer>
            {state.isPreview && <Button onClick={handleSubmit(onSubmit)} disabled={state.uploading}>{ state.uploading ? 'Espere ...' : 'Guardar' }</Button>}
            <Button color="gray" disabled={state.uploading} onClick={handleClose}>Cerrar</Button>
        </Modal.Footer>
    </Modal>
}




export default FileManager